import { useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { SignOut, LockKey, CaretUpDown, UserCircle } from "@phosphor-icons/react";
import { useAuth } from "../lib/auth";
import { useBranding } from "../lib/branding";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator,
} from "./ui/dropdown-menu";

const ROLE_LABEL = {
  super_admin: "Super Admin", owner: "Owner", hr: "HR",
  manager: "Manager", employee: "Employee",
};
const ROLE_COLOR = {
  super_admin: "bg-[#0F172A]/10 text-[#0F172A]",
  owner: "bg-[#0EA5E9]/10 text-[#0EA5E9]",
  hr: "bg-[#0284C7]/10 text-[#0284C7]",
  manager: "bg-[#16A34A]/10 text-[#16A34A]",
  employee: "bg-[#475569]/10 text-[#475569]",
};

export default function UserMenu({ variant = "sidebar" }) {
  const { user, logout } = useAuth();
  const branding = useBranding();
  const navigate = useNavigate();
  const [sheetOpen, setSheetOpen] = useState(false);
  if (!user) return null;

  const isSuper = user.role === "super_admin";
  const tenantName = isSuper ? "Platform · All tenants" : (branding.company_display_name || "—");
  const initial = user.name?.[0]?.toUpperCase() || "?";

  const doLogout = async () => {
    setSheetOpen(false);
    await logout();
    navigate("/login");
  };

  /* Identity block reused by both the sidebar dropdown and the mobile sheet. */
  const identity = (
    <div className="px-1 py-1">
      <div className="flex items-center gap-2 mb-2">
        <UserCircle size={18} weight="duotone" color="#0EA5E9" />
        <div className="text-sm font-semibold truncate" data-testid="user-menu-name">{user.name}</div>
      </div>
      <div className="text-xs text-[#475569] font-mono-data truncate mb-3" data-testid="user-menu-email">{user.email}</div>
      <div className="flex items-center justify-between gap-2 mb-2">
        <span className="small-label">Role</span>
        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${ROLE_COLOR[user.role] || ROLE_COLOR.employee}`}
              data-testid="user-menu-role">
          {ROLE_LABEL[user.role] || user.role}
        </span>
      </div>
      <div className="flex items-start justify-between gap-2">
        <span className="small-label shrink-0">Workspace</span>
        <span className="text-xs text-[#0F172A] text-right truncate" data-testid="user-menu-tenant">{tenantName}</span>
      </div>
    </div>
  );

  /* ----- MOBILE: stable bottom sheet (does NOT dismiss on page scroll) ----- */
  if (variant === "mobile") {
    return (
      <>
        <button
          data-testid="user-menu-trigger"
          onClick={() => setSheetOpen(true)}
          aria-label="Open profile menu"
          className="w-9 h-9 rounded-full bg-gradient-to-br from-[#0EA5E9] to-[#0284C7] flex items-center justify-center text-white font-semibold text-sm shrink-0"
        >
          {initial}
        </button>

        {sheetOpen && createPortal(
          <div className="fixed inset-0 z-[60] lg:hidden" data-testid="user-menu-sheet">
            <div className="absolute inset-0 bg-black/40" onClick={() => setSheetOpen(false)} />
            <div
              className="absolute left-0 right-0 bottom-0 bg-white rounded-t-2xl shadow-2xl"
              style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
            >
              <div className="flex justify-center pt-3">
                <div className="w-10 h-1.5 rounded-full bg-[#E2E8F0]" />
              </div>
              <div className="px-5 pt-3">{identity}</div>
              <div className="border-t border-[#E2E8F0] mt-3 px-3 py-2">
                <button
                  data-testid="user-menu-change-password"
                  onClick={() => { setSheetOpen(false); navigate("/change-password"); }}
                  className="w-full flex items-center gap-3 px-3 py-3.5 rounded-xl text-[#475569] hover:bg-[#F1F5F9] active:bg-[#F1F5F9] text-sm font-medium"
                >
                  <LockKey size={18} /> Change password
                </button>
                <button
                  data-testid="user-menu-logout"
                  onClick={doLogout}
                  className="w-full flex items-center gap-3 px-3 py-3.5 rounded-xl text-[#DC2626] hover:bg-[#FEF2F2] active:bg-[#FEF2F2] text-sm font-medium"
                >
                  <SignOut size={18} /> Log out
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
      </>
    );
  }

  /* ----- DESKTOP SIDEBAR: dropdown (anchored in a fixed sidebar, no scroll issue) ----- */
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="user-menu-trigger"
          className="w-full flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-[#F1F5F9] transition-colors"
        >
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#0EA5E9] to-[#0284C7] flex items-center justify-center text-white font-semibold text-sm shrink-0">
            {initial}
          </div>
          <div className="flex-1 min-w-0 text-left">
            <div className="text-sm font-medium truncate">{user.name}</div>
            <div className="text-xs text-[#475569] capitalize">{ROLE_LABEL[user.role] || user.role}</div>
          </div>
          <CaretUpDown size={16} className="text-[#94A3B8] shrink-0" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" side="top" className="w-64" data-testid="user-menu-content">
        <div className="px-2 py-2">{identity}</div>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => navigate("/change-password")}
          data-testid="user-menu-change-password"
          className="cursor-pointer"
        >
          <LockKey size={16} /> Change password
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={doLogout}
          data-testid="user-menu-logout"
          className="cursor-pointer text-[#DC2626] focus:text-[#DC2626]"
        >
          <SignOut size={16} /> Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
